import React, { useContext } from "react";
import * as NavigationMenu from "@radix-ui/react-navigation-menu";
import classNames from "classnames";
import { CaretDownIcon, ExternalLinkIcon } from "@radix-ui/react-icons";
import "./navbar-styles.css";
import { AccountContext } from "../App.tsx";
import { Link } from "react-router-dom";
import { Box } from "@radix-ui/themes";

interface ListItemProps {
  title: string;
  children: React.ReactNode;
  to: string;
}

const ListItem = ({ title, children, to, ...props }: ListItemProps) => {
  return (
    <li>
      <NavigationMenu.Link asChild>
        <Link className={classNames("ListItemLink")} {...props} to={to}>
          <div className="ListItemHeading">{title}</div>
          <div className="ListItemText">
            <Box>{children as string}</Box>
          </div>
        </Link>
      </NavigationMenu.Link>
    </li>
  );
};

const Navbar = () => {
  const [account] = useContext(AccountContext);

  return (
    <NavigationMenu.Root className="NavigationMenuRoot">
      <NavigationMenu.List className="NavigationMenuList">
        <NavigationMenu.Item>
          <Link className="NavigationMenuLink" to="/">
            <Box>Home</Box>
          </Link>
        </NavigationMenu.Item>
        <NavigationMenu.Item>
          <NavigationMenu.Trigger className="NavigationMenuTrigger">
            <Box>Game and Story</Box>{" "}
            <CaretDownIcon className="CaretDown" aria-hidden />
          </NavigationMenu.Trigger>
          <NavigationMenu.Content className="NavigationMenuContent">
            <ul className="List one">
              <ListItem title={"Introduction"} to="/introduction">
                Set and setting: Defcon in the mix.
              </ListItem>
              <ListItem title={"Context"} to="/context">
                Deepfakes, AI and truth.
              </ListItem>
              <ListItem title={"AP news item, July 29, 2024"} to="/ap">
                Serbian prime minister, Viloš Mučević, believed to have been
                attacked.
              </ListItem>
              <ListItem
                title={"NSA casts doubt on leaked recordings"}
                to="/nsa"
              >
                Team "Joshua" fingerprints tampering in Serbian incident.
              </ListItem>
              <ListItem title={"The world stage, and a new player"} to="/world">
                Op-ed from tech luminary Eliza Warren.
              </ListItem>
              <ListItem title={"MIT: Evidence of LLM tampering"} to="/mit">
                Rising percentage of news footage edited without publisher's
                knowledge.
              </ListItem>
              <ListItem
                title={"Fingerpointing at NATO summit"}
                to="/fingerpointing"
              >
                Russia, Argentina and South Korea offer bold claims though
                evidence is scarce.
              </ListItem>
              <ListItem
                title={
                  "Mysterious Cyber Attacks Cripple Global Banking Systems"
                }
                to="/flash1"
              >
                Fed to issue press release imminently.
              </ListItem>
              <ListItem
                title={"Fabricated Climate Crisis Report Sparks Uproar"}
                to="/flash2"
              >
                Scientists call for increased scrutiny before reports are
                publicized, champion academic publishing standards.
              </ListItem>
              <ListItem
                title={
                  "Deepfake Video of Presidential Candidate Causes Political Turmoil"
                }
                to="/flash3"
              >
                Politicians blame big tech; big tech blames the politicians;
                media conglomerates caught in the crossfire.
              </ListItem>
              <ListItem
                title={"AI Escalates Tensions in the Middle East"}
                to="/flash4"
              >
                Tensions mount in a region already on the brink of war.
              </ListItem>
              <ListItem
                title={"AI-Driven Stock Market Manipulation Discovered"}
                to="/flash5"
              >
                Banks across the world react to new vector of attack.
              </ListItem>
              <ListItem
                title={
                  "AI Impersonates World Leaders in Coordinated Disinformation Campaign"
                }
                to="/flash6"
              >
                Attack leaves NATO picking up the pieces.
              </ListItem>
              <ListItem
                title={"AI Deepfake Sparks International Celebrity Scandal"}
                to="/long1"
              >
                Harrington denies all allegations.
              </ListItem>
              <ListItem
                title={"AI Hoax Leads to Mass Panic at Music Festival"}
                to="/long2"
              >
                Security concerns surrounding fake messages of unknown origin
                spreading across the world.
              </ListItem>
              <ListItem
                title={"AI-Created Art Auction Causes Uproar in the Art World"}
                to="/long4"
              >
                Art world disrupted, and questions arise about the value of
                authenticity.
              </ListItem>
              <ListItem
                title={"AI Fashion Trends Cause Market Turmoil"}
                to="/long3"
              >
                Economies at risk as continued false media postings threaten to
                dominate news cycle.
              </ListItem>
              <ListItem title={"Rubio impersonated"} to="/rubio">
                Impostor uses AI to impersonate Rubio and contact foreign and US
                officials.
              </ListItem>
              <ListItem title={"Odds and ends"} to="/odds">
                Miscellaneous thoughts from the dungeon masters.
              </ListItem>
            </ul>
          </NavigationMenu.Content>
        </NavigationMenu.Item>
        <NavigationMenu.Item>
          <Link className="NavigationMenuLink" to="/account">
            Account
          </Link>
        </NavigationMenu.Item>
        {account && (
          <NavigationMenu.Item>
            <Link className="NavigationMenuLink" to="/game">
              Enter game
            </Link>
          </NavigationMenu.Item>
        )}
        <NavigationMenu.Item>
          <Link className="NavigationMenuLink" to="/leaderboard">
            Leaderboard
          </Link>
        </NavigationMenu.Item>
        <NavigationMenu.Item>
          <Link className="NavigationMenuLink" to="/issues">
            Known issues
          </Link>
        </NavigationMenu.Item>
        <NavigationMenu.Item>
          {/* External: a plain <a>, not a react-router <Link> — Link only does
              client-side routing and would try to resolve this as an in-app
              path. rel="noopener" also stops the opened page from reaching
              back through window.opener. */}
          <a
            className="NavigationMenuLink"
            href="https://github.com/reali7y-over-run/defcon34-ror3-wiki"
            target="_blank"
            rel="noopener noreferrer"
          >
            Badge wiki
            <ExternalLinkIcon
              aria-hidden
              style={{ marginLeft: 5, verticalAlign: "-1px" }}
            />
            <span className="ro-visually-hidden"> (opens in a new tab)</span>
          </a>
        </NavigationMenu.Item>
        <NavigationMenu.Indicator className="NavigationMenuIndicator">
          <div className="Arrow" />
        </NavigationMenu.Indicator>
      </NavigationMenu.List>

      <div className="ViewportPosition">
        <NavigationMenu.Viewport className="NavigationMenuViewport" />
      </div>
    </NavigationMenu.Root>
  );
};

export { Navbar };
